import {
  Avatar,
  Box,
  CollapseCard,
  CopyToClipboard,
  FlexBox,
  Icon,
  IconButton,
  SandboxIframe,
  Spinner,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { skipToken } from '@reduxjs/toolkit/query/react';
import type {
  CustomCardIntegrationSanitized,
  ReadConversationCustomCardApiResponse,
} from 'lib-shared-types-api';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { useColorMode } from 'app/utils/colorMode';
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';
import {
  CARD_V2,
  getCardStyle,
  getStylesheet,
} from 'components/shared/util/customCard';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { VisibilityToggle } from '../../components/visibilityToggle/visibilityToggle';
import { ConversationCardsSettings } from '../../conversationSidebar';

import styles from './customCardsItem.module.scss';

const documentationUrl = 'https://support.dixa.help/en/articles/216';

export type CustomCardsItemProps = {
  card: CustomCardIntegrationSanitized;
  conversationCardsSettings: ConversationCardsSettings;
  isMinimized?: boolean;
  updateConversationCards: (payload: {
    key: string;
    value: boolean;
  }) => Promise<unknown>;
  csid?: number | null;
};

interface BuildContext {
  content: string;
  createdAt?: string | null;
  cardVersion?: string;
  className?: string;
}

export function CustomCardsItem({
  card,
  isMinimized = false,
  conversationCardsSettings,
  updateConversationCards,
  csid,
}: CustomCardsItemProps): ReactElement {
  const cardKey = card.id;
  const cardTitle = card.title || 'Card failed to load';
  const cardStyle = getCardStyle(card.integration_type);
  const avatar = <Avatar icon={cardStyle.icon} />;

  const [collapseKey] = useState<string>(() => cardKey);
  const [collapseErrorKey] = useState<string>(() => `${cardKey}_errorCollapse`);
  const [isErrorCopied, setIsErrorCopied] = useState<boolean>(false);
  const flags = useFeatureFlag([
    'allow-same-origin-in-sandboxed-custom-card-iframe',
  ]);

  const { colorMode } = useColorMode();

  useEffect(() => {
    const timeoutId = isErrorCopied
      ? window.setTimeout(() => setIsErrorCopied(false), 3000)
      : undefined;
    return () => window.clearTimeout(timeoutId);
  }, [isErrorCopied]);

  const getError = (response: ReadConversationCustomCardApiResponse) => {
    const cardError = response.data.error;

    return {
      id: card.id,
      title: card?.title,
      type: cardError?.type,
      code: cardError?.code,
      message: cardError?.message ?? 'Unknown error',
      isRetriable: cardError?.is_retriable,
    };
  };

  const buildCardContent = useCallback(
    (context: BuildContext) => {
      const cardDom = document.createElement('div');

      if (context.className) {
        cardDom.className = context.className;
      }

      // TODO: [TEAMS-12079] Investigate improper use of innerHTML
      cardDom.innerHTML = context.content;

      const anchorElements = cardDom.querySelectorAll('a');

      for (let index = 0; index < anchorElements.length; index++) {
        const anchor = anchorElements[index];
        if (anchor instanceof HTMLAnchorElement) {
          anchor.target = '_blank';
          anchor.rel = 'noopener noreferrer';
        }
      }

      const getSandboxPermissions = () => {
        const permissions = [
          SandboxIframe.defaultSandboxParams,
          'allow-popups-to-escape-sandbox',
          ...(flags.allowSameOriginInSandboxedCustomCardIframe
            ? ['allow-same-origin']
            : []),
        ];

        return permissions.join(' ');
      };

      // Cards that meet this condition need to be wrapped
      // in an iframe to be backward compatible
      return context.createdAt && context.cardVersion === CARD_V2 ? (
        <SandboxIframe
          headContent={getStylesheet()}
          content={cardDom.outerHTML}
          sandbox={getSandboxPermissions()}
          colorMode={colorMode}
        />
      ) : (
        <SafeInnerHtmlSetter
          data-testid="cardContent"
          element="div"
          content={cardDom.innerHTML}
          noSanitize
        />
      );
    },
    [colorMode, flags.allowSameOriginInSandboxedCustomCardIframe],
  );

  const getCardOpenState = useCallback(
    (type: string) => {
      const key = type === 'card' ? collapseKey : collapseErrorKey;
      return (
        typeof conversationCardsSettings[key] === 'undefined' ||
        !!conversationCardsSettings[key]
      );
    },
    [collapseKey, collapseErrorKey, conversationCardsSettings],
  );

  const toggleArrowClick = useCallback(
    (type) => {
      const isCardType = type === 'card';
      const key = isCardType ? collapseKey : collapseErrorKey;
      updateConversationCards({
        key,
        value: isCardType
          ? !getCardOpenState('card')
          : !getCardOpenState('cardError'),
      });
    },
    [collapseKey, collapseErrorKey, getCardOpenState, updateConversationCards],
  );

  const isCardOpen = getCardOpenState('card');
  const isCardErrorOpen = getCardOpenState('cardError');

  const {
    data: renderedCustomCard,
    isFetching: isRenderedCustomCardFetching,
    isError: hasRenderedCustomCardError,
    refetch: refetchRenderedCustomCard,
  } = conversationsApi.useReadConversationCustomCardQuery(
    csid && card?.id && !isMinimized && isCardOpen
      ? {
          csid,
          hookId: card.id,
        }
      : skipToken,
  );

  // Handles refetching the custom card when it is toggled open, but not on
  // initial mount.
  useUpdateEffect(() => {
    if (csid && card?.id && !isMinimized && isCardOpen) {
      refetchRenderedCustomCard();
    }
  }, [isCardOpen]);

  const error = renderedCustomCard?.data?.error
    ? getError(renderedCustomCard)
    : undefined;

  const hasError = !!error || hasRenderedCustomCardError;
  const hasContent =
    !isRenderedCustomCardFetching && !!renderedCustomCard?.data?.content;

  const errorAvatar = (
    <div className={styles.errorAvatar}>
      <Avatar
        size={isMinimized ? '24' : '32'}
        color={{
          contentColor: colors.neutral['500'],
          backgroundColor: isMinimized
            ? colors.neutral['000']
            : colors.neutral['100'],
        }}
        icon={cardStyle.icon}
        className={styles.errorAvatarIcon}
      />
      {(isMinimized || !isCardOpen) && (
        <Icon
          name="exclamation-circle-filled"
          className={styles.errorAvatarIndicator}
          color={colors.red[500]}
        />
      )}
    </div>
  );

  if (isMinimized) {
    return hasError ? (
      <div className={styles.errorAvatarMinimized}>{errorAvatar}</div>
    ) : (
      <div className={styles.minimizedAvatar}>{avatar}</div>
    );
  }

  return (
    <CollapseCard
      data-testid="cardCollapse"
      onToggle={() => toggleArrowClick('card')}
      isOpen={isCardOpen}
    >
      <CollapseCard.Header
        wrap="normal"
        avatar={hasError ? errorAvatar : avatar}
        slotAfterTitle={<VisibilityToggle cardId={cardKey} />}
      >
        {cardTitle}
      </CollapseCard.Header>
      <CollapseCard.Content>
        <CollapseCard.Line className={styles.collapseContent}>
          {isRenderedCustomCardFetching ? (
            <FlexBox
              justifyContent="center"
              margin="300"
              data-testid="cardCollapseSpinner"
            >
              <Spinner />
            </FlexBox>
          ) : hasError ? (
            <>
              <Box
                className={styles.errorMessage}
                data-testid="cardCollapseError"
              >
                <FlexBox
                  className={styles.errorMessageHeader}
                  flexDirection="row"
                  gap="250"
                  onClick={() => toggleArrowClick('cardError')}
                >
                  <Box className={styles.errorMessageHeaderIcon}>
                    <Icon
                      name="exclamation-circle-filled"
                      color={colors.red[500]}
                    />
                  </Box>
                  <Box>
                    <Text.Paragraph>
                      {error?.code && `${error.code}: `}
                      {error?.message}
                    </Text.Paragraph>
                    <Text.Paragraph
                      variant="3"
                      className={styles.errorMessageHeaderLink}
                    >
                      {isCardErrorOpen ? 'Hide details' : 'Show details'}
                    </Text.Paragraph>
                  </Box>
                </FlexBox>
                {isCardErrorOpen && (
                  <Box className={styles.errorMessageContent}>
                    <CopyToClipboard
                      textToCopy={JSON.stringify(error, null, 2)}
                      tooltip={false}
                      onClick={() => setIsErrorCopied(true)}
                      className={styles.errorMessageCopyIconWrapper}
                    >
                      <Tooltip
                        content={isErrorCopied ? 'Copied' : 'Copy response'}
                      >
                        <IconButton
                          icon={isErrorCopied ? 'done' : 'copy'}
                          variant="secondary"
                          className={styles.errorMessageCopyIcon}
                        />
                      </Tooltip>
                    </CopyToClipboard>
                    <pre className={styles.errorMessageContentCode}>
                      {JSON.stringify(error, null, 2)}
                    </pre>
                  </Box>
                )}
              </Box>
              <Text.Paragraph variant="4">
                Read more about{' '}
                <Text.ExternalLink
                  href={documentationUrl}
                  className={styles.errorMessageFooterLink}
                >
                  integrating custom cards
                </Text.ExternalLink>
                .
              </Text.Paragraph>
              <Text.Paragraph variant="4">
                Alternatively, contact our technical support team.
              </Text.Paragraph>
            </>
          ) : hasContent ? (
            buildCardContent({
              content: renderedCustomCard.data.content || '',
              createdAt: card.created_at,
              cardVersion: card.card_version,
              className: cardStyle?.className,
            })
          ) : (
            <CollapseCard.Status>No content</CollapseCard.Status>
          )}
        </CollapseCard.Line>
      </CollapseCard.Content>
    </CollapseCard>
  );
}
